import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, CheckCircle2, Clock, AlertCircle, Trash2, Plus, Calendar as CalendarIcon,
  ChevronLeft, ChevronRight, AlertTriangle, MapPin, User, Package, Bell,
  Truck, Ship, Briefcase, Star, X, Users
} from 'lucide-react';

// Event type configuration
const EVENT_TYPES = {
  task: { label: 'Task', color: 'bg-slate-500', icon: CheckCircle2 },
  meeting: { label: 'Meeting', color: 'bg-indigo-500', icon: Users },
  appointment: { label: 'Appointment', color: 'bg-blue-500', icon: Briefcase },
  job_booking: { label: 'Job Booking', color: 'bg-green-500', icon: Package },
  memorial_collection: { label: 'Memorial Collection', color: 'bg-red-500', icon: AlertTriangle },
  delivery: { label: 'Delivery', color: 'bg-orange-500', icon: Truck },
  shipment_eta: { label: 'Shipment ETA', color: 'bg-purple-500', icon: Ship },
  custom: { label: 'Custom Event', color: 'bg-gray-500', icon: Star }
};

// Overdue Alert Banner Component
function OverdueAlertBanner({ overdueData, onViewOverdue, onDismiss }) {
  if (!overdueData || overdueData.total_overdue === 0) return null;
  
  const memorialCount = overdueData.memorial_collections_overdue;
  const hasMemorials = memorialCount > 0;
  
  return (
    <div className={`${hasMemorials ? 'bg-red-600 animate-pulse' : 'bg-orange-500'} text-white px-4 py-3 mb-4 rounded-lg shadow-lg`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6" />
          <div>
            <p className="font-bold">
              {hasMemorials 
                ? `URGENT: ${memorialCount} Memorial Collection${memorialCount > 1 ? 's' : ''} Overdue!`
                : `${overdueData.total_overdue} Overdue Item${overdueData.total_overdue > 1 ? 's' : ''}`
              }
            </p>
            <p className="text-sm opacity-90">
              {hasMemorials 
                ? 'These need immediate attention - click to view details'
                : 'Click to view and resolve overdue items'
              }
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={onViewOverdue}
            data-testid="view-overdue-btn"
          >
            View All
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onDismiss}
            className="text-white hover:bg-white/20"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Overdue Popup Modal
function OverduePopupModal({ isOpen, onClose, overdueEvents, onMarkCollected }) {
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            Overdue Items Requiring Attention
          </DialogTitle>
          <DialogDescription>
            These items have passed their deadline and need immediate action.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 mt-4">
          {overdueEvents.map((event) => {
            const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.task;
            const Icon = typeConfig.icon;
            
            return (
              <div 
                key={event.id} 
                className={`p-4 rounded-lg border-2 ${
                  event.event_type === 'memorial_collection' 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-orange-300 bg-orange-50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4" />
                      <Badge className={`${typeConfig.color} text-white`}>
                        {typeConfig.label}
                      </Badge>
                      <Badge variant="destructive">
                        {event.days_overdue} day{event.days_overdue !== 1 ? 's' : ''} overdue
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-lg">{event.title}</h4>
                    {event.description && (
                      <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                    )}
                    {event.deadline_date && (
                      <p className="text-sm text-red-600 mt-2">
                        Deadline was: {new Date(event.deadline_date).toLocaleDateString('en-GB', {
                          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                  
                  {event.event_type === 'memorial_collection' && (
                    <Button 
                      onClick={() => onMarkCollected(event.id)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`mark-collected-${event.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark Collected
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Week View Calendar Component with Time Grid
function WeekView({ weekData, currentDate, onDateChange, onEventClick, onDayClick, currentUserEmail }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm
  
  const goToPrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    onDateChange(newDate);
  };
  
  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    onDateChange(newDate);
  };
  
  const goToToday = () => {
    onDateChange(new Date());
  };
  
  // Get the Monday of the current week
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };
  
  const monday = getMonday(currentDate);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
  
  const formatWeekRange = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    const startMonth = start.toLocaleDateString('en-GB', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-GB', { month: 'short' });
    
    if (startMonth === endMonth) {
      return `${start.getDate()} - ${end.getDate()} ${startMonth} ${start.getFullYear()}`;
    }
    return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth} ${start.getFullYear()}`;
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  // Get event time position (returns percentage from top)
  const getEventPosition = (event) => {
    const eventDate = event.start_date || event.scheduled_date || event.due_date;
    if (!eventDate) return null;
    
    const dt = new Date(eventDate);
    const hours = dt.getHours();
    const minutes = dt.getMinutes();
    
    // If it's midnight (all-day event), return null
    if (hours === 0 && minutes === 0 && event.is_all_day !== false) return null;
    
    // Calculate position: 7am = 0%, 7pm = 100%
    const totalMinutes = (hours - 7) * 60 + minutes;
    const maxMinutes = 12 * 60; // 12 hours (7am to 7pm)
    return Math.max(0, Math.min(100, (totalMinutes / maxMinutes) * 100));
  };
  
  // Get event duration height
  const getEventHeight = (event) => {
    if (!event.end_date || !event.start_date) return 8; // Default height
    
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    const durationMinutes = (end - start) / (1000 * 60);
    const maxMinutes = 12 * 60;
    const heightPercent = (durationMinutes / maxMinutes) * 100;
    return Math.max(6, Math.min(50, heightPercent)); // Min 6%, max 50%
  };
  
  // Format time for display
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    const hours = dt.getHours();
    const minutes = dt.getMinutes();
    if (hours === 0 && minutes === 0) return '';
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };
  
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 bg-slate-50 border-b">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrevWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToNextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToToday}>
            Today
          </Button>
        </div>
        <h2 className="text-lg font-semibold">{formatWeekRange()}</h2>
        <div className="w-24" /> {/* Spacer for alignment */}
      </div>
      
      {/* Week Header - Days */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b">
        <div className="p-2 border-r bg-slate-50"></div>
        {days.map((day, index) => (
          <div 
            key={day} 
            className={`p-2 text-center border-r last:border-r-0 ${
              isToday(weekDates[index]) ? 'bg-blue-50' : ''
            }`}
          >
            <div className="text-xs text-slate-500 uppercase">{day}</div>
            <div className={`text-lg font-semibold ${
              isToday(weekDates[index]) ? 'text-blue-600' : ''
            }`}>
              {weekDates[index].getDate()}
            </div>
          </div>
        ))}
      </div>
      
      {/* All-day events row */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-slate-50/50">
        <div className="p-1 text-xs text-slate-400 border-r text-center">All day</div>
        {weekDates.map((date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateKey = `${year}-${month}-${day}`;
          const dayEvents = (weekData?.days?.[dateKey] || []).filter(e => {
            const pos = getEventPosition(e);
            return pos === null; // All-day events
          });
          
          return (
            <div 
              key={dateKey} 
              className="p-1 border-r last:border-r-0 min-h-[40px] cursor-pointer hover:bg-slate-100"
              onClick={() => onDayClick(date)}
            >
              {dayEvents.slice(0, 2).map((event) => {
                const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.task;
                const isOverdue = event.is_overdue || event.status === 'overdue';
                
                return (
                  <div
                    key={event.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                    className={`px-1 py-0.5 rounded text-xs cursor-pointer mb-1 truncate ${
                      isOverdue ? 'bg-red-500 text-white animate-pulse' : `${typeConfig.color} text-white`
                    }`}
                  >
                    {event.title}
                  </div>
                );
              })}
              {dayEvents.length > 2 && (
                <div className="text-xs text-slate-400">+{dayEvents.length - 2} more</div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Time Grid */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] overflow-y-auto" style={{ maxHeight: '500px' }}>
        {/* Time labels column */}
        <div className="border-r">
          {hours.map((hour) => (
            <div key={hour} className="h-12 border-b text-xs text-slate-400 text-right pr-2 pt-1">
              {hour}:00
            </div>
          ))}
        </div>
        
        {/* Day columns with events */}
        {weekDates.map((date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateKey = `${year}-${month}-${day}`;
          const dayEvents = (weekData?.days?.[dateKey] || []).filter(e => {
            const pos = getEventPosition(e);
            return pos !== null; // Timed events only
          });
          
          return (
            <div 
              key={dateKey} 
              className={`relative border-r last:border-r-0 ${isToday(date) ? 'bg-blue-50/30' : ''}`}
              onClick={() => onDayClick(date)}
            >
              {/* Hour grid lines */}
              {hours.map((hour) => (
                <div key={hour} className="h-12 border-b border-slate-100 hover:bg-slate-50 cursor-pointer" />
              ))}
              
              {/* Timed events */}
              {dayEvents.map((event) => {
                const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.task;
                const isOverdue = event.is_overdue || event.status === 'overdue';
                const position = getEventPosition(event);
                const height = getEventHeight(event);
                const time = formatTime(event.start_date);
                
                return (
                  <div
                    key={event.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                    className={`absolute left-1 right-1 px-1 py-0.5 rounded text-xs cursor-pointer overflow-hidden ${
                      isOverdue 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : event.is_personal 
                          ? `${typeConfig.color} text-white border-2 border-dashed border-white/50`
                          : `${typeConfig.color} text-white`
                    }`}
                    style={{
                      top: `${position}%`,
                      minHeight: `${height}%`,
                      zIndex: 10
                    }}
                    data-testid={`event-${event.id}`}
                  >
                    <div className="font-medium truncate">
                      {time && <span className="opacity-80">{time} </span>}
                      {event.title}
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-1 opacity-80 truncate">
                        <MapPin className="w-2 h-2" />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Create/Edit Event Dialog
function EventDialog({ isOpen, onClose, event, onSave, users, orders, defaultDate, onViewOrder }) {
  const isEdit = !!event?.id;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'task',
    priority: 'normal',
    assigned_to: '',
    order_id: '',
    start_date: '',
    deadline_date: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    is_personal: false,
    is_all_day: true,
    location: '',
    notes: ''
  });
  
  useEffect(() => {
    if (event) {
      // Extract time from datetime if present
      let startTime = '';
      let endTime = '';
      if (event.start_date) {
        const startDt = new Date(event.start_date);
        if (startDt.getHours() !== 0 || startDt.getMinutes() !== 0) {
          startTime = `${String(startDt.getHours()).padStart(2, '0')}:${String(startDt.getMinutes()).padStart(2, '0')}`;
        }
      }
      if (event.end_date) {
        const endDt = new Date(event.end_date);
        if (endDt.getHours() !== 0 || endDt.getMinutes() !== 0) {
          endTime = `${String(endDt.getHours()).padStart(2, '0')}:${String(endDt.getMinutes()).padStart(2, '0')}`;
        }
      }
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || 'task',
        priority: event.priority || 'normal',
        assigned_to: event.assigned_to || '',
        order_id: event.order_id || '',
        start_date: event.start_date ? new Date(event.start_date).toISOString().split('T')[0] : '',
        deadline_date: event.deadline_date ? new Date(event.deadline_date).toISOString().split('T')[0] : '',
        scheduled_date: event.scheduled_date ? new Date(event.scheduled_date).toISOString().split('T')[0] : '',
        start_time: startTime,
        end_time: endTime,
        is_personal: event.is_personal || false,
        is_all_day: event.is_all_day !== false && !startTime,
        location: event.location || '',
        notes: event.notes || ''
      });
    } else {
      // Reset form for new event - use defaultDate if provided
      const dateToUse = defaultDate ? new Date(defaultDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setFormData({
        title: '',
        description: '',
        event_type: 'task',
        priority: 'normal',
        assigned_to: '',
        order_id: '',
        start_date: dateToUse,
        deadline_date: '',
        scheduled_date: '',
        start_time: '',
        end_time: '',
        is_personal: false,
        is_all_day: true,
        location: '',
        notes: ''
      });
    }
  }, [event, isOpen, defaultDate]);
  
  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    // Build datetime with time if provided
    let startDateTime = null;
    let endDateTime = null;
    
    if (formData.start_date) {
      if (formData.start_time && !formData.is_all_day) {
        startDateTime = new Date(`${formData.start_date}T${formData.start_time}:00`).toISOString();
      } else {
        startDateTime = new Date(formData.start_date).toISOString();
      }
    }
    
    if (formData.start_date && formData.end_time && !formData.is_all_day) {
      endDateTime = new Date(`${formData.start_date}T${formData.end_time}:00`).toISOString();
    }
    
    const payload = {
      ...formData,
      start_date: startDateTime,
      end_date: endDateTime,
      deadline_date: formData.deadline_date ? new Date(formData.deadline_date).toISOString() : null,
      scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : null,
      due_date: formData.deadline_date ? new Date(formData.deadline_date).toISOString() : null
    };
    
    // Remove temp fields and null/empty values
    delete payload.start_time;
    delete payload.end_time;
    Object.keys(payload).forEach(key => {
      if (payload[key] === '' || payload[key] === null) {
        delete payload[key];
      }
    });
    
    onSave(payload, event?.id);
  };
  
  const showDeadlineFields = ['memorial_collection', 'delivery', 'shipment_eta'].includes(formData.event_type);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the event details below.' : 'Fill in the details for your new event.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Event Type */}
          <div>
            <Label>Event Type</Label>
            <Select 
              value={formData.event_type} 
              onValueChange={(v) => setFormData({...formData, event_type: v})}
            >
              <SelectTrigger data-testid="event-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EVENT_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${config.color}`} />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Title */}
          <div>
            <Label>Title *</Label>
            <Input 
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Event title"
              data-testid="event-title-input"
            />
          </div>
          
          {/* Description */}
          <div>
            <Label>Description</Label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Event description"
              rows={2}
            />
          </div>
          
          {/* Date Fields - Different layout for memorial collections */}
          {formData.event_type === 'memorial_collection' ? (
            <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800">Memorial Collection Dates</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Planned Collection Date *</Label>
                  <Input 
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value, start_date: e.target.value})}
                    data-testid="event-scheduled-input"
                  />
                  <p className="text-xs text-slate-600 mt-1">When you plan to collect it (shows on calendar)</p>
                </div>
                <div>
                  <Label className="text-red-700">Funeral Date (Must collect by)</Label>
                  <Input 
                    type="date"
                    value={formData.deadline_date}
                    onChange={(e) => setFormData({...formData, deadline_date: e.target.value})}
                    data-testid="event-deadline-input"
                  />
                  <p className="text-xs text-red-600 mt-1">Alerts if not collected by this date</p>
                </div>
              </div>
            </div>
          ) : showDeadlineFields ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <Label className="text-red-600">Deadline (Must be done by)</Label>
                  <Input 
                    type="date"
                    value={formData.deadline_date}
                    onChange={(e) => setFormData({...formData, deadline_date: e.target.value})}
                    data-testid="event-deadline-input"
                  />
                </div>
              </div>
              <div>
                <Label>Scheduled Date (When we plan to do it)</Label>
                <Input 
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  data-testid="event-scheduled-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* All Day Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>All Day Event</Label>
                  <p className="text-xs text-slate-500">Toggle off to set specific times</p>
                </div>
                <Switch 
                  checked={formData.is_all_day}
                  onCheckedChange={(v) => setFormData({...formData, is_all_day: v, start_time: '', end_time: ''})}
                />
              </div>
              
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="event-date-input"
                  />
                </div>
                {!formData.is_all_day && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Start Time</Label>
                      <Input 
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                        data-testid="event-start-time-input"
                      />
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Input 
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                        data-testid="event-end-time-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Priority */}
          <div>
            <Label>Priority</Label>
            <Select 
              value={formData.priority} 
              onValueChange={(v) => setFormData({...formData, priority: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Assign To */}
          <div>
            <Label>Assign To</Label>
            <Select 
              value={formData.assigned_to || 'unassigned'} 
              onValueChange={(v) => setFormData({...formData, assigned_to: v === 'unassigned' ? '' : v})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.email}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Link to Order - Special for Memorial Collections */}
          {formData.event_type === 'memorial_collection' ? (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-800 font-medium">Link to Memorial Order</Label>
              <p className="text-xs text-blue-600 mb-2">Connect this collection to an order to access the collection form</p>
              <Select 
                value={formData.order_id || 'none'} 
                onValueChange={(v) => setFormData({...formData, order_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked order</SelectItem>
                  {orders.slice(0, 50).map((order) => (
                    <SelectItem key={order.id} value={order.id}>
                      #{order.order_number} - {order.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.order_id && isEdit && (
                <Button 
                  type="button"
                  variant="outline" 
                  className="mt-3 w-full bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    onClose();
                    onViewOrder(formData.order_id);
                  }}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Go to Order & Fill Collection Form
                </Button>
              )}
            </div>
          ) : orders && orders.length > 0 && (
            <div>
              <Label>Link to Order (Optional)</Label>
              <Select 
                value={formData.order_id || 'none'} 
                onValueChange={(v) => setFormData({...formData, order_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked order</SelectItem>
                  {orders.slice(0, 50).map((order) => (
                    <SelectItem key={order.id} value={order.id}>
                      #{order.order_number} - {order.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Location */}
          <div>
            <Label>Location</Label>
            <Input 
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Event location"
            />
          </div>
          
          {/* Personal Event Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Personal Event</Label>
              <p className="text-xs text-slate-500">Personal events are highlighted differently</p>
            </div>
            <Switch 
              checked={formData.is_personal}
              onCheckedChange={(v) => setFormData({...formData, is_personal: v})}
            />
          </div>
          
          {/* Notes */}
          <div>
            <Label>Additional Notes</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-event-btn">
            {isEdit ? 'Update Event' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Reminder Card Component
function ReminderCard({ title, events, colorClass, icon: Icon }) {
  if (!events || events.length === 0) return null;
  
  return (
    <Card className={`border-l-4 ${colorClass}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title} ({events.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {events.slice(0, 3).map((event) => (
            <div key={event.id} className="text-sm p-2 bg-slate-50 rounded">
              <div className="font-medium">{event.title}</div>
              {event.deadline_date && (
                <div className="text-xs text-slate-500">
                  Deadline: {new Date(event.deadline_date).toLocaleDateString('en-GB')}
                </div>
              )}
            </div>
          ))}
          {events.length > 3 && (
            <p className="text-xs text-slate-500">+{events.length - 3} more</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Tasks Page Component
function Tasks() {
  const navigate = useNavigate();
  const { token, API, user } = useContext(AuthContext);
  
  // State
  const [activeTab, setActiveTab] = useState('calendar');
  const [tasks, setTasks] = useState([]);
  const [weekData, setWeekData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // Dialogs
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Overdue alerts
  const [overdueData, setOverdueData] = useState(null);
  const [showOverdueBanner, setShowOverdueBanner] = useState(true);
  const [showOverduePopup, setShowOverduePopup] = useState(false);
  
  // Reminders
  const [reminders, setReminders] = useState(null);

  // Fetch functions
  const fetchTasks = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, [API, token, filter]);

  const fetchWeekData = useCallback(async () => {
    try {
      const dateStr = currentDate.toISOString();
      const response = await axios.get(`${API}/calendar/week/${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeekData(response.data);
    } catch (error) {
      console.error('Failed to fetch week data:', error);
    }
  }, [API, token, currentDate]);

  const fetchOverdue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/overdue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverdueData(response.data);
      
      // Auto-show popup if there are overdue memorial collections
      if (response.data.memorial_collections_overdue > 0) {
        setShowOverduePopup(true);
      }
    } catch (error) {
      console.error('Failed to fetch overdue:', error);
    }
  }, [API, token]);

  const fetchReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/upcoming-reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    }
  }, [API, token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => ['admin', 'staff', 'company_admin'].includes(u.role)));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [API, token]);

  const fetchOrders = useCallback(async () => {
    try {
      // Always scope the calendar's order dropdown to the current user's own
      // company — even for super_admins, who would otherwise see every
      // company's orders (multi-tenant leak). `filter_company_id` is
      // honoured by the backend only for super_admins; for regular admins /
      // staff it's ignored because they're already scoped by company.
      const params = { limit: 500 };
      if (user?.company_id) {
        params.filter_company_id = user.company_id;
      }
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setOrders(response.data.orders || response.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }, [API, token, user?.company_id]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTasks(),
        fetchWeekData(),
        fetchOverdue(),
        fetchReminders(),
        fetchUsers(),
        fetchOrders()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Refresh when date or filter changes
  useEffect(() => {
    fetchWeekData();
  }, [currentDate, fetchWeekData]);

  useEffect(() => {
    fetchTasks();
  }, [filter, fetchTasks]);

  // Event handlers
  const handleSaveEvent = async (data, eventId) => {
    try {
      if (eventId) {
        await axios.patch(`${API}/tasks/${eventId}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/tasks`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created');
      }
      
      setEventDialogOpen(false);
      setSelectedEvent(null);
      
      // Refresh data
      fetchTasks();
      fetchWeekData();
      fetchOverdue();
      fetchReminders();
    } catch (error) {
      toast.error('Failed to save event');
      console.error(error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      
      setEventDialogOpen(false);
      setSelectedEvent(null);
      
      fetchTasks();
      fetchWeekData();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, 
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task completed!');
      fetchTasks();
      fetchWeekData();
      fetchOverdue();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleMarkCollected = async (taskId) => {
    try {
      await axios.post(`${API}/calendar/mark-collected/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Memorial marked as collected!');
      fetchTasks();
      fetchWeekData();
      fetchOverdue();
      setShowOverduePopup(false);
    } catch (error) {
      toast.error('Failed to mark as collected');
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSelectedDate(null);
    setEventDialogOpen(true);
  };

  const handleDayClick = (date) => {
    setSelectedEvent(null);
    setSelectedDate(date);
    setEventDialogOpen(true);
  };

  const openNewEventDialog = () => {
    setSelectedEvent(null);
    setSelectedDate(null);
    setEventDialogOpen(true);
  };

  // Stats calculations
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const overdueTasks = tasks.filter(t => t.status === 'overdue' || t.is_overdue);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'normal': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'low': return 'bg-slate-100 text-slate-700 border-slate-300';
      default: return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Overdue Alert Banner */}
        {showOverdueBanner && (
          <OverdueAlertBanner 
            overdueData={overdueData}
            onViewOverdue={() => setShowOverduePopup(true)}
            onDismiss={() => setShowOverdueBanner(false)}
          />
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Tasks & Calendar</h1>
            <p className="text-slate-600">Manage tasks, appointments, collections, and deadlines</p>
          </div>
          <Button onClick={openNewEventDialog} data-testid="new-event-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Event
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-blue-600">{pendingTasks.length}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{completedTasks.length}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{overdueData?.total_overdue || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Due Today</p>
                  <p className="text-2xl font-bold text-orange-600">{reminders?.due_today?.length || 0}</p>
                </div>
                <Bell className="w-8 h-8 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Reminders */}
        {reminders && (reminders.due_today?.length > 0 || reminders.due_in_3_days?.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <ReminderCard 
              title="Due Today" 
              events={reminders.due_today} 
              colorClass="border-red-500" 
              icon={AlertTriangle}
            />
            <ReminderCard 
              title="Due in 3 Days" 
              events={reminders.due_in_3_days} 
              colorClass="border-orange-500" 
              icon={Clock}
            />
            <ReminderCard 
              title="Due in 7 Days" 
              events={reminders.due_in_7_days} 
              colorClass="border-yellow-500" 
              icon={CalendarIcon}
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="calendar" data-testid="calendar-tab">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="list" data-testid="list-tab">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Task List
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {loading ? (
              <div className="text-center py-12">
                <div className="text-lg text-slate-600">Loading calendar...</div>
              </div>
            ) : (
              <WeekView 
                weekData={weekData}
                currentDate={currentDate}
                onDateChange={setCurrentDate}
                onEventClick={handleEventClick}
                onDayClick={handleDayClick}
                currentUserEmail={user?.email}
              />
            )}
            
            {/* Legend */}
            <div className="mt-4 p-4 bg-white rounded-lg shadow">
              <h3 className="text-sm font-medium mb-3">Event Types</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(EVENT_TYPES).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${config.color}`} />
                    <span className="text-sm">{config.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 border-l pl-3">
                  <div className="w-4 h-4 rounded bg-slate-500 border-2 border-dashed border-slate-300" />
                  <span className="text-sm">Personal</span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Task List Tab */}
          <TabsContent value="list">
            {/* Filter Tabs */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                size="sm"
              >
                All ({tasks.length})
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
                size="sm"
              >
                Pending ({pendingTasks.length})
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilter('completed')}
                size="sm"
              >
                Completed ({completedTasks.length})
              </Button>
            </div>

            {/* Event Type Filter */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(EVENT_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tasks List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="text-lg text-slate-600">Loading tasks...</div>
              </div>
            ) : tasks.length === 0 ? (
              <Card className="border-0 shadow-lg text-center py-12">
                <CardContent>
                  <CheckCircle2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-lg text-slate-600">No tasks found</p>
                  <p className="text-sm text-slate-500 mt-2">Click "New Event" to create your first task</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {tasks
                  .filter(t => eventTypeFilter === 'all' || t.event_type === eventTypeFilter)
                  .map((task) => {
                    const typeConfig = EVENT_TYPES[task.event_type] || EVENT_TYPES.task;
                    const Icon = typeConfig.icon;
                    const isOverdue = task.status === 'overdue' || task.is_overdue;
                    
                    return (
                      <Card
                        key={task.id}
                        className={`border-0 shadow-lg transition-all ${
                          isOverdue 
                            ? 'ring-2 ring-red-500 animate-pulse' 
                            : task.status === 'completed' 
                              ? 'opacity-60' 
                              : 'hover:shadow-xl'
                        }`}
                        data-testid={`task-${task.id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex-1 w-full">
                              <div className="flex items-start gap-3 mb-3">
                                <div className={`mt-1 p-1 rounded ${typeConfig.color}`}>
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className={`text-lg font-semibold ${
                                      task.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-900'
                                    }`}>
                                      {task.title}
                                    </h3>
                                    {task.is_personal && (
                                      <Badge variant="outline" className="text-xs">Personal</Badge>
                                    )}
                                  </div>
                                  
                                  <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                                  
                                  <div className="flex flex-wrap gap-2">
                                    <Badge className={`${typeConfig.color} text-white`}>
                                      {typeConfig.label}
                                    </Badge>
                                    <Badge className={`${getPriorityColor(task.priority)} border`}>
                                      {task.priority} priority
                                    </Badge>
                                    {isOverdue && (
                                      <Badge variant="destructive">OVERDUE</Badge>
                                    )}
                                    <Badge variant="outline" className="capitalize">
                                      {task.status}
                                    </Badge>
                                    {task.deadline_date && (
                                      <span className="text-xs text-slate-500">
                                        Deadline: {new Date(task.deadline_date).toLocaleDateString('en-GB')}
                                      </span>
                                    )}
                                    {task.location && (
                                      <span className="text-xs text-slate-500 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {task.location}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                              {task.status === 'pending' && (
                                <Button
                                  onClick={() => handleCompleteTask(task.id)}
                                  className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                                  size="sm"
                                  data-testid={`complete-task-${task.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Complete
                                </Button>
                              )}
                              {task.event_type === 'memorial_collection' && task.order_id && task.status !== 'completed' && (
                                <Button
                                  onClick={() => navigate(`/orders/${task.order_id}`)}
                                  className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700"
                                  size="sm"
                                >
                                  <Package className="w-4 h-4 mr-2" />
                                  Collection Form
                                </Button>
                              )}
                              {task.event_type === 'memorial_collection' && task.status !== 'completed' && (
                                <Button
                                  onClick={() => handleMarkCollected(task.id)}
                                  variant="outline"
                                  className="flex-1 sm:flex-none border-green-600 text-green-600 hover:bg-green-50"
                                  size="sm"
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Mark Collected
                                </Button>
                              )}
                              <Button
                                onClick={() => handleEventClick(task)}
                                variant="outline"
                                size="sm"
                                className="flex-1 sm:flex-none"
                              >
                                Edit
                              </Button>
                              {task.order_id && task.event_type !== 'memorial_collection' && (
                                <Button
                                  onClick={() => navigate(`/orders/${task.order_id}`)}
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 sm:flex-none"
                                >
                                  View Order
                                </Button>
                              )}
                              <Button
                                onClick={() => handleDeleteEvent(task.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Event Dialog */}
        <EventDialog 
          isOpen={eventDialogOpen}
          onClose={() => {
            setEventDialogOpen(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          event={selectedEvent}
          defaultDate={selectedDate}
          onSave={handleSaveEvent}
          onViewOrder={(orderId) => navigate(`/orders/${orderId}`)}
          users={users}
          orders={orders}
        />

        {/* Overdue Popup Modal */}
        <OverduePopupModal 
          isOpen={showOverduePopup}
          onClose={() => setShowOverduePopup(false)}
          overdueEvents={overdueData?.overdue_events || []}
          onMarkCollected={handleMarkCollected}
        />
      </div>
    </div>
  );
}

export default Tasks;
